import { Injectable, Injector, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { tap, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Globals } from '../../globals.service';
import { LogService } from '../utility/log.service';
import { NotificationService } from '../ui/notification.service';
import { LogType } from '../utility/logtype';
import { UserRole } from 'src/app/typescript/userRole';
import { LoaderService } from '../loader/loader.service';

@Injectable({ providedIn: 'root' })
export class HttpMiddlewareService {
  private http = inject(HttpClient);
  private injector = inject(Injector);
  private globals = inject(Globals);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private loaderSevice = inject(LoaderService);

  private _logService: LogService | null = null;
  private _logServiceLookupFailed = false;
  private get logService(): LogService | null {
    if (this._logServiceLookupFailed) return null;
    if (!this._logService) {
      try {
        this._logService = this.injector.get(LogService, null);
      } catch {
        this._logServiceLookupFailed = true;
        return null;
      }
    }
    return this._logService;
  }

  post(url: string, body: any = null, isWSidNeeded?: boolean, isLoaderNeeded: boolean = false) {
    if (isLoaderNeeded) {
      this.loaderSevice.showLoader();
    }
    return this.http
      .post(url, body, {
        headers: this.globals.getRole().includes(UserRole.SUPERADMIN)
          ? this.globals.getSAHeaders(isWSidNeeded)
          : this.globals.getHeaders(),
      })
      .pipe(
        tap({
          next: (response) => {
            this.logService?.log(response, LogType.RESPONSE);
            if (isLoaderNeeded) {
              this.loaderSevice.hideLoader();
            }
          },
          error: (error) => {
            this.logService?.log(error, LogType.ERROR);
            this.notificationService.showError(error);
            this.navigateToLogIn(error);
            if (isLoaderNeeded) {
              this.loaderSevice.hideLoader();
            }
          },
        })
      );
  }

  put(url: string, body: any = null, isWSidNeeded?: boolean) {
    return this.http
      .put(url, body, {
        headers: this.globals.getRole().includes(UserRole.SUPERADMIN)
          ? this.globals.getSAHeaders(isWSidNeeded)
          : this.globals.getHeaders(),
      })
      .pipe(
        tap({
          next: (response) => this.logService?.log(response, LogType.RESPONSE),
          error: (error) => {
            this.logService?.log(error, LogType.ERROR);
            this.notificationService.showError(error);
            this.navigateToLogIn(error);
          },
        })
      );
  }

  putFile(url: string, body: any = null, forced?: boolean, isWSidNeeded?: boolean) {
    const formData: FormData = new FormData();
    formData.append('file', body, body.name);
    let headers = new HttpHeaders();
    headers = headers.append('Authorization', 'Bearer ' + localStorage.getItem('admin_token'));
    headers = headers.delete('Content-Type');
    headers = headers.append('Accept-Language', this.globals.getLang());
    if (forced) {
      headers = headers.append('x-force', 'true');
    }
    if (isWSidNeeded) {
      headers = headers.append('x-workShopId', this.globals.SELECTED_WORKSHOP.id.toString());
    }
    return this.http.put(url, formData, { headers: headers }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.notificationService.showError(error);
          this.navigateToLogIn(error);
        },
      })
    );
  }

  postB(url: string, body: any = null) {
    return this.http
      .post(url, body, { headers: this.globals.getSAHeaders(false), observe: 'response', withCredentials: true })
      .pipe(
        tap((response) => this.logService?.log(response, LogType.RESPONSE)),
        map((response) => {
          this.globals.countVehicle = parseInt(response.headers.get('x-content-count'));
          return response.body;
        })
      );
  }

  postImage(url: string, body: any = null) {
    return this.http.post(url, body, { headers: this.globals.getImageHeaders() }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.notificationService.showError(error);
          this.navigateToLogIn(error);
        },
      })
    );
  }

  postImageWithXforce(url: string, body: any = null, id) {
    return this.http.post(url, body, { headers: this.globals.getImageWithXforce(id) }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.notificationService.showError(error);
          this.navigateToLogIn(error);
        },
      })
    );
  }

  postSA(url: string, body: any = null, isWSidNeeded?: boolean, forced?: boolean) {
    return this.http.post(url, body, { headers: this.globals.getSAHeaders(isWSidNeeded, forced) }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.notificationService.showError(error);
          this.navigateToLogIn(error);
        },
      })
    );
  }

  postSAFile(url: string, body: any = null) {
    return this.http
      .post(url, body, { headers: this.globals.getSAHeaders(), responseType: 'blob', observe: 'response' })
      .pipe(
        tap((response) => this.logService?.log(response, LogType.RESPONSE)),
        map((response) => ({
          file: response,
          filename: response.headers.get('Content-Disposition'),
        }))
      );
  }

  getSAFile(url: string) {
    let headers = this.globals.getSAHeaders();
    headers = headers.set('Content-Type', 'application/pdf');
    return this.http.get(url, { headers: headers, responseType: 'blob', observe: 'response' }).pipe(
      tap((response) => this.logService?.log(response, LogType.RESPONSE)),
      map((response) => ({
        file: response,
        filename: response.headers.get('Content-Disposition'),
      }))
    );
  }

  public postFile(url: string, body: any = null, isSA) {
    return this.http
      .post(url, body, { headers: this.globals.getMagicHeaders(isSA), responseType: 'blob', observe: 'response' })
      .pipe(
        tap((response) => this.logService?.log(response, LogType.RESPONSE)),
        map((response) => ({
          file: response,
          filename: response.headers.get('Content-Disposition'),
        }))
      );
  }

  public getFile(url: string) {
    return this.http
      .get(url, { headers: this.globals.getExcelHeaders(), responseType: 'blob', observe: 'response' })
      .pipe(
        tap((response) => this.logService?.log(response, LogType.RESPONSE)),
        map((response) => ({
          file: response,
          filename: response.headers.get('Content-Disposition'),
        }))
      );
  }

  postRes(url: string, body: any = null) {
    return this.http
      .post(url, body, { headers: this.globals.getSAHeaders(), observe: 'response', withCredentials: true })
      .pipe(
        tap((response) => this.logService?.log(response, LogType.RESPONSE)),
        map((response) => {
          this.globals.contentcount = parseInt(response.headers.get('x-content-count'));
          return response.body;
        })
      );
  }

  postResB(url: string, body: any = null) {
    return this.http
      .post(url, body, { headers: this.globals.getSAHeaders(), observe: 'response', withCredentials: true })
      .pipe(
        tap((response) => this.logService?.log(response, LogType.RESPONSE)),
        map((response) => {
          this.globals.countVehicle = parseInt(response.headers.get('x-content-count'));
          return response.body;
        })
      );
  }

  postRA(url: string, body: any = null, alreadyReserved: boolean) {
    return this.http.post(url, body, { headers: this.globals.getRAHeaders(alreadyReserved) }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.navigateToLogIn(error);
          this.notificationService.showError(error);
        },
      })
    );
  }

  deleteForced(url: string, forced: boolean) {
    return this.http.delete(url, { headers: this.globals.getRAHeaders(forced) }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.navigateToLogIn(error);
          this.notificationService.showError(error);
        },
      })
    );
  }

  get(url: string, isWSidNeeded?: boolean, isLoaderNeeded: boolean = false) {
    if (isLoaderNeeded) {
      this.loaderSevice.showLoader();
    }
    return this.http
      .get(url, {
        headers: this.globals.getRole().includes(UserRole.SUPERADMIN)
          ? this.globals.getSAHeaders(isWSidNeeded)
          : this.globals.getHeaders(),
      })
      .pipe(
        tap({
          next: (response) => {
            if (isLoaderNeeded) {
              this.loaderSevice.hideLoader();
            }
            this.logService?.log(response, LogType.RESPONSE);
          },
          error: (error) => {
            this.logService?.log(error, LogType.ERROR);
            this.navigateToLogIn(error);
            this.notificationService.showError(error);
            if (isLoaderNeeded) {
              this.loaderSevice.hideLoader();
            }
          },
        })
      );
  }

  getSA(url: string, isWSidNeeded?: boolean, isLoaderNeeded: boolean = false) {
    if (isLoaderNeeded) {
      this.loaderSevice.showLoader();
    }
    return this.http.get(url, { headers: this.globals.getSAHeaders(isWSidNeeded) }).pipe(
      tap({
        next: (response) => {
          if (isLoaderNeeded) {
            this.loaderSevice.hideLoader();
          }
          this.logService?.log(response, LogType.RESPONSE);
        },
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.navigateToLogIn(error);
          this.notificationService.showError(error);
          if (isLoaderNeeded) {
            this.loaderSevice.hideLoader();
          }
        },
      })
    );
  }

  patch(url: string, body: any, isWSidNeeded?: boolean, forced?: boolean, needHttpResponse: boolean = false) {
    if (needHttpResponse) {
      return this.http
        .patch(url, body, {
          headers: this.globals.getRole().includes(UserRole.SUPERADMIN)
            ? this.globals.getSAHeaders(isWSidNeeded, forced)
            : this.globals.getHeaders(forced),
          observe: 'response',
        })
        .pipe(
          tap({
            next: (response) => this.logService?.log(response, LogType.RESPONSE),
            error: (error) => {
              this.logService?.log(error, LogType.ERROR);
              this.navigateToLogIn(error);
              this.notificationService.showError(error);
            },
          })
        );
    }

    return this.http
      .patch(url, body, {
        headers: this.globals.getRole().includes(UserRole.SUPERADMIN)
          ? this.globals.getSAHeaders(isWSidNeeded, forced)
          : this.globals.getHeaders(forced),
      })
      .pipe(
        tap({
          next: (response) => this.logService?.log(response, LogType.RESPONSE),
          error: (error) => {
            this.logService?.log(error, LogType.ERROR);
            this.navigateToLogIn(error);
            this.notificationService.showError(error);
          },
        })
      );
  }

  patchWorkshop(url: string, body: any, workshopid) {
    return this.http.patch(url, body, { headers: this.globals.getWorkshopHeaders(null, null, workshopid) }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.navigateToLogIn(error);
          this.notificationService.showError(error);
        },
      })
    );
  }

  patchB(url: string, body: any) {
    return this.http.patch(url, body, { headers: this.globals.getSAHeaders(false) }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.navigateToLogIn(error);
          this.notificationService.showError(error);
        },
      })
    );
  }

  patchSA(url: string, body: any, isWSidNeeded?: boolean, fromTyreHotel?: boolean, forced: boolean = false) {
    return this.http.patch(url, body, { headers: this.globals.getSAHeaders(isWSidNeeded, forced, fromTyreHotel) }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.navigateToLogIn(error);
          this.notificationService.showError(error);
        },
      })
    );
  }

  patchRA(url: string, body: any, alreadyReserved: boolean) {
    return this.http.patch(url, body, { headers: this.globals.getRAHeaders(alreadyReserved) }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.navigateToLogIn(error);
          this.notificationService.showError(error);
        },
      })
    );
  }

  delete(url: string, isWSidNeeded?: boolean, isForce?: boolean) {
    return this.http
      .delete(url, {
        headers: this.globals.getRole().includes(UserRole.SUPERADMIN)
          ? this.globals.getSAHeaders(isWSidNeeded, isForce)
          : this.globals.getHeaders(),
      })
      .pipe(
        tap({
          next: (response) => this.logService?.log(response, LogType.RESPONSE),
          error: (error) => {
            this.logService?.log(error, LogType.ERROR);
            this.navigateToLogIn(error);
            this.notificationService.showError(error);
          },
        })
      );
  }

  deleteSA(url: string, isWSidNeeded?: boolean) {
    return this.http.delete(url, { headers: this.globals.getSAHeaders(isWSidNeeded) }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.navigateToLogIn(error);
          this.notificationService.showError(error);
        },
      })
    );
  }

  deleteB(url: string) {
    return this.http.delete(url, { headers: this.globals.getSAHeaders(false) }).pipe(
      tap({
        next: (response) => this.logService?.log(response, LogType.RESPONSE),
        error: (error) => {
          this.logService?.log(error, LogType.ERROR);
          this.navigateToLogIn(error);
          this.notificationService.showError(error);
        },
      })
    );
  }

  deleteExposedHeaders(url: string, isWSidNeeded?: boolean, isForce?: boolean) {
    return this.http
      .delete(url, {
        headers: this.globals.getRole().includes(UserRole.SUPERADMIN)
          ? this.globals.getSAHeaders(isWSidNeeded, isForce)
          : this.globals.getHeaders(),
        observe: 'response',
      })
      .pipe(
        tap({
          next: (response) => this.logService?.log(response, LogType.RESPONSE),
          error: (error) => {
            this.logService?.log(error, LogType.ERROR);
            this.navigateToLogIn(error);
            this.notificationService.showError(error);
          },
        })
      );
  }

  navigateToLogIn(error: any) {
    if (error.status === 401) {
      localStorage.removeItem(this.globals.KEY_TOKEN);
      localStorage.removeItem(this.globals.KEY_USER);
      this.router.navigate([this.globals.loginUrl]);
    }
  }
}
